import { Badge, Box, Checkbox, Group, Text, Tooltip } from '@optiaxiom/react';
import { IconChevronDown, IconChevronRight } from '@optimizely/axiom-icons';
import { createColumnHelper } from '@tanstack/react-table';

import type { NotificationRow } from '../../types/notification';
import { getCategory, getNotificationVisuals, getPriority, getPriorityIntent } from '../../utils';

import { ActionsCell } from './cells/ActionsCell';
import { TimestampCell } from './cells/TimestampCell';
import { UnreadIndicatorCell } from './cells/UnreadIndicatorCell';

const columnHelper = createColumnHelper<NotificationRow>();

type ColumnCallbacks = {
  onActivate: (row: NotificationRow) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onDelete: (id: string) => void;
  onGroupMarkRead: (groupId: string) => void;
  onGroupDelete: (groupId: string) => void;
};

export function createColumns(callbacks: ColumnCallbacks) {
  return [
    columnHelper.display({
      id: 'select',
      cell: ({ row }) => {
        const isGroup = row.getCanExpand();
        return (
          <Group gap="8" alignItems="center">
            <UnreadIndicatorCell isRead={row.original.is_read} />
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={() => row.toggleSelected()}
              aria-label={row.getIsSelected() ? 'Unselect notification' : 'Select notification'}
            />
            {isGroup && (
              <Box
                display="flex"
                alignItems="center"
                cursor="pointer"
                ml="6"
                onClick={() => row.toggleExpanded()}
                aria-label={row.getIsExpanded() ? 'Collapse group' : 'Expand group'}
              >
                {row.getIsExpanded() ? <IconChevronDown /> : <IconChevronRight />}
              </Box>
            )}
          </Group>
        );
      },
      header: ({ table }) => (
        <Group gap="8" alignItems="center">
          <UnreadIndicatorCell isRead />
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all notifications"
          />
        </Group>
      ),
      size: 80,
      enableResizing: false,
    }),
    columnHelper.display({
      id: 'notification',
      header: 'Notification',
      cell: ({ row }) => {
        const data = row.original;
        const isGroup = row.getCanExpand();
        const visuals = getNotificationVisuals(data.type);

        return (
          <Group gap="4" alignItems="start" w="full">
            {row.depth > 0 && <Box flex="none" style={{ width: 20 }} />}
            {visuals && (
              <Box color={visuals.color} mt="2" flex="none">
                <visuals.Icon />
              </Box>
            )}
            <Group flexDirection="column" gap="2" overflow="hidden" w="full">
              <Group gap="8" alignItems="center">
                <Text
                  fontWeight={data.is_read ? '400' : '600'}
                  truncate
                  cursor={isGroup ? 'pointer' : 'default'}
                  onClick={isGroup ? () => row.toggleExpanded() : () => callbacks.onActivate(data)}
                >
                  {data.title}
                </Text>
                {isGroup && data.count > 1 && <Badge intent="neutral">{data.count}</Badge>}
              </Group>
              {!isGroup && data.message && (
                <Tooltip content={data.message} delayDuration={0}>
                  <Text fontSize="sm" color="fg.tertiary" lineClamp="1">
                    {data.message}
                  </Text>
                </Tooltip>
              )}
            </Group>
          </Group>
        );
      },
      size: 700,
    }),
    columnHelper.display({
      id: 'category',
      header: 'Category',
      cell: ({ row }) => <Badge intent="neutral">{getCategory(row.original.type)}</Badge>,
      size: 150,
    }),
    columnHelper.accessor('created_at', {
      header: 'Timestamp',
      cell: ({ getValue }) => <TimestampCell createdAt={new Date(getValue())} />,
      size: 80,
    }),
    columnHelper.display({
      id: 'priority',
      header: 'Priority',
      cell: ({ row }) => {
        const priority = getPriority(row.original.type);
        return <Badge intent={getPriorityIntent(priority)}>{priority}</Badge>;
      },
      size: 80,
    }),
    columnHelper.display({
      id: 'actions',
      cell: ({ row }) => {
        const data = row.original;
        if (row.getCanExpand()) {
          return (
            <ActionsCell
              notification={data}
              isGroup
              onMarkRead={() => callbacks.onGroupMarkRead(data.group_id)}
              onMarkUnread={() => {}}
              onDelete={() => callbacks.onGroupDelete(data.group_id)}
            />
          );
        }
        return (
          <ActionsCell
            notification={data}
            onMarkRead={() => callbacks.onMarkRead(data.id)}
            onMarkUnread={() => callbacks.onMarkUnread(data.id)}
            onDelete={() => callbacks.onDelete(data.id)}
          />
        );
      },
      size: 60,
    }),
  ];
}
